import { useGenerateReport } from "@homesite/hooks";
import type { ReportFilterPanelProps, ReportItem } from "@homesite/ui";
import { ReportsListPage, useToast } from "@homesite/ui";
import { createRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { env } from "../lib/env";
import { rootRoute } from "./__root";

// Reports are generated on-demand; no server-side list endpoint exists.
const EMPTY_REPORTS: ReportItem[] = [];

export const reportsListRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/reports",
	component: function ReportsList() {
		const navigate = useNavigate();
		const { toast } = useToast();
		const { generate, isPending, isError, error, reset } = useGenerateReport(
			env.VITE_API_URL,
		);

		const [format, setFormat] = useState<"pdf" | "csv">("pdf");
		const [kind, setKind] = useState<"event" | "loan" | "batch">("loan");
		const [target, setTarget] = useState("");
		const [dateFrom, setDateFrom] = useState<string | null>(null);
		const [dateTo, setDateTo] = useState<string | null>(null);

		// biome-ignore lint/correctness/useExhaustiveDependencies: mount-only default
		useEffect(() => {
			if (dateFrom === null && dateTo === null) {
				const today = new Date();
				const thirtyDaysAgo = new Date(today);
				thirtyDaysAgo.setDate(today.getDate() - 30);
				setDateFrom(thirtyDaysAgo.toISOString().split("T")[0] ?? null);
				setDateTo(today.toISOString().split("T")[0] ?? null);
			}
		}, []);

		function handleSubmit(): void {
			reset();
			const ext = format === "pdf" ? "pdf" : "csv";
			const safeDateFrom = dateFrom ?? "all";
			const filename = `homesite-${kind}-${target.slice(0, 10)}-${safeDateFrom}.${ext}`;
			generate({ format, kind, target, dateFrom, dateTo })
				.then(() => {
					toast.success("Report downloaded", { description: filename });
				})
				.catch((err: unknown) => {
					const message =
						err instanceof Error ? err.message : "Report generation failed";
					toast.error(message);
				});
		}

		const filterPanel: ReportFilterPanelProps = {
			format,
			onFormatChange: setFormat,
			kind,
			onKindChange: setKind,
			target,
			onTargetChange: (v) => {
				setTarget(v);
				if (isError) reset();
			},
			dateFrom,
			onDateFromChange: setDateFrom,
			dateTo,
			onDateToChange: setDateTo,
			onSubmit: handleSubmit,
			submitting: isPending,
			error: isError && error ? error.message : null,
		};

		return (
			<ReportsListPage
				reports={EMPTY_REPORTS}
				onNavigateEvents={() => navigate({ to: "/events" })}
				onNavigateLoans={() => navigate({ to: "/loans" })}
				filterPanel={filterPanel}
			/>
		);
	},
});
